<template>
  <div
    class="tw-relative rotator-wrapper"
    @mouseenter="startPreload"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dragstart.prevent
    @click.capture="onClickCapture"
  >
    <template v-if="hasRotator">
      <img
        v-for="(item, i) in validAngles"
        :key="item.angle"
        v-show="i === currentIndex"
        class="card-img-top rotator-angle"
        draggable="false"
        :style="{ transform: 'scale(' + zoom + ')' }"
        :src="(preload || i === currentIndex) ? item.url : PLACEHOLDER"
        :alt="alt"
        :loading="i === 0 ? 'lazy' : 'eager'"
        decoding="async"
        @load="onLoad(i, item.angle)"
        @error="onError(item.angle)"
      >
      <button type="button" class="rotator-arrow rotator-arrow-left" @click.stop.prevent="prev" aria-label="Előző szög"><i class="fas fa-chevron-left"></i></button>
      <button type="button" class="rotator-arrow rotator-arrow-right" @click.stop.prevent="next" aria-label="Következő szög"><i class="fas fa-chevron-right"></i></button>
      <div class="rotator-dots">
        <button v-for="(item, i) in validAngles" :key="item.angle" type="button" class="rotator-dot" :class="{ 'rotator-dot-active': i === currentIndex }" @click.stop.prevent="go(i)" :aria-label="'Szög ' + (i + 1)"></button>
      </div>
      <div v-if="showSpinner" class="rotator-spinner" aria-hidden="true"><i class="fas fa-circle-notch fa-spin"></i></div>
    </template>
    <img v-else class="card-img-top" :src="fallback" :alt="alt" loading="lazy" decoding="async">
  </div>
</template>

<script>
  import Vue from 'vue';
  import { Component, Prop } from 'vue-property-decorator';

  const PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  @Component
  export default class CarImageRotator extends Vue {
    @Prop({ type: Array, default: null }) angles;
    @Prop({ type: String, default: '' }) fallback;
    @Prop({ type: String, default: '' }) alt;
    @Prop({ type: Number, default: 1.25 }) zoom;

    currentIndex = 0;
    removedAngles = [];
    preload = false;
    loaded = {};

    // Drag-to-rotate state (pointer = mouse + touch)
    isDragging = false;
    dragStartX = 0;
    dragStartIndex = 0;
    dragged = false;

    get PLACEHOLDER() {
      return PLACEHOLDER;
    }

    get validAngles() {
      return Array.isArray(this.angles)
        ? this.angles.filter(a => !this.removedAngles.includes(a.angle))
        : [];
    }

    get hasRotator() {
      return Array.isArray(this.angles) && this.validAngles.length > 1;
    }

    get currentReady() {
      const cur = this.validAngles[this.currentIndex];
      return cur ? this.loaded[cur.angle] === true : true;
    }

    get showSpinner() {
      return this.hasRotator && !this.currentReady;
    }

    startPreload() {
      this.preload = true;
    }

    select(i) {
      this.startPreload();
      this.currentIndex = i;
    }

    prev() {
      if (!this.validAngles.length) return;
      const newIndex = (this.currentIndex - 1 + this.validAngles.length) % this.validAngles.length;
      this.select(newIndex);
    }

    next() {
      if (!this.validAngles.length) return;
      const newIndex = (this.currentIndex + 1) % this.validAngles.length;
      this.select(newIndex);
    }

    go(i) {
      this.select(i);
    }

    // --- Drag / swipe to rotate (mouse + touch via Pointer Events) ---
    // The card is wrapped in an <a>, so a drag must NOT navigate: we set `dragged`
    // and swallow the click that follows the gesture (onClickCapture). A plain tap
    // (no movement) leaves dragged=false and the card link works as before.
    onPointerDown(e) {
      if (!this.hasRotator) return;
      if (e.button != null && e.button !== 0) return;
      if (e.target && e.target.closest && e.target.closest('.rotator-arrow, .rotator-dot')) return;
      e.preventDefault();
      this.isDragging     = true;
      this.dragged        = false;
      this.dragStartX     = e.clientX;
      this.dragStartIndex = this.currentIndex;
      this.startPreload();
      const el = e.currentTarget;
      if (e.pointerId != null && el && el.setPointerCapture) {
        try { el.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
      }
    }

    onPointerMove(e) {
      if (!this.isDragging) return;
      const n = this.validAngles.length;
      if (!n) return;
      const dx = e.clientX - this.dragStartX;
      if (Math.abs(dx) > 6) this.dragged = true;
      const width      = (this.$el && this.$el.offsetWidth) || 300;
      const pxPerFrame = width / n;
      const offset     = Math.round(dx / pxPerFrame);
      const newIndex   = ((this.dragStartIndex + offset) % n + n) % n;
      if (newIndex !== this.currentIndex) {
        this.select(newIndex);
      }
    }

    onPointerUp(e) {
      this.isDragging = false;
      const el = e && e.currentTarget;
      if (e && e.pointerId != null && el && el.releasePointerCapture) {
        try { el.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
      }
    }

    onClickCapture(e) {
      // Suppress the synthetic click that fires after a drag so the card link
      // doesn't open. preventDefault kills the <a href> default; stopPropagation
      // keeps it from reaching the anchor's @click handler.
      if (this.dragged) {
        e.preventDefault();
        e.stopPropagation();
        this.dragged = false;
      }
    }

    onLoad(i, angle) {
      // The 1px PLACEHOLDER (used for not-yet-preloaded angles) also fires @load.
      // Only count a load when the element actually held the real URL, otherwise the
      // placeholder would mark the angle "loaded" and the spinner gate would clear early.
      if (!this.preload && i !== this.currentIndex) {
        return;
      }
      this.loaded = { ...this.loaded, [angle]: true };
    }

    onError(angle) {
      // Capture the selected angle VALUE before removal, so the index follows the
      // same angle after validAngles shifts.
      const selectedAngle = (this.validAngles[this.currentIndex] || {}).angle;

      this.removedAngles = [...this.removedAngles, angle];

      const remaining = this.validAngles;
      const last = Math.max(0, remaining.length - 1);
      const resolve = (a, fallbackIdx) => {
        const idx = remaining.findIndex((x) => x.angle === a);
        return idx === -1 ? Math.min(fallbackIdx, last) : idx;
      };

      this.currentIndex = resolve(selectedAngle, this.currentIndex);
    }
  }
</script>

<style scoped>
  .rotator-wrapper {
    position: relative;
    overflow: hidden;
    width: 100%;
    flex: 0 0 auto;                 /* the flex card must NOT resize the image area */
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
    cursor: grab;
    user-select: none;
    touch-action: pan-y;            /* vertical swipe scrolls the page; horizontal = rotate */
  }
  .rotator-wrapper:active {
    cursor: grabbing;
  }
  .rotator-wrapper::before {        /* fixed 8/5 (375/600) ratio via in-flow block — flex-immune */
    content: "";
    display: block;
    padding-top: 62.5%;
  }

  .rotator-wrapper img.card-img-top {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .rotator-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    z-index: 10;
    color: rgba(0, 0, 0, 0.55);
    transition: color 0.15s;
  }

  .rotator-arrow i {
    font-size: 26px;
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.7);
  }

  .rotator-arrow:hover {
    color: rgba(0, 0, 0, 0.85);
  }

  .rotator-arrow:focus {
    outline: none;
  }

  .rotator-arrow-left {
    left: 6px;
  }

  .rotator-arrow-right {
    right: 6px;
  }

  .rotator-dots {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    z-index: 10;
  }

  .rotator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(200, 200, 200, 0.7);
    border: none;
    padding: 0;
    cursor: pointer;
    transition: background 0.15s;
  }

  .rotator-dot:focus {
    outline: none;
  }

  .rotator-dot-active {
    background: #0d6efd;
  }

  .rotator-dot:hover:not(.rotator-dot-active) {
    background: rgba(150, 150, 150, 0.9);
  }

  .rotator-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9;
    pointer-events: none;
  }

  .rotator-spinner i {
    font-size: 24px;
    color: #6c757d;
  }
</style>
